import {
  useIsMobile,
  WSCircle,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSPanel,
  WSText,
  WSButton,
  WSBadge
} from "@wingspanhq/fe-component-library";
import React from "react";
import cn from "classnames";
import { IDashboardToDo } from "../../utils/dashboardUtils";
import styles from "./DashboardToDo.module.scss";
import { useUpdateActivity } from "../../../query/users/mutations";
import { useUserId } from "../../../query/hooks/helpers";
import { ToDoStatus } from "@wingspanhq/users/dist/lib/interfaces";
type Props = {
  size?: "S" | "M" | "L";
  recommended?: boolean;
  todoType: string;
} & IDashboardToDo &
  WSElementProps;

export const ToDoEmptyState: React.FC = () => {
  const isMobile = useIsMobile();
  return (
    <WSPanel>
      <WSFlexBox.CenterY
        wrap="nowrap"
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "flex-start" : "center"}
      >
        <WSCircle.Icon
          icon="successMark"
          mr={isMobile ? undefined : "M"}
          mb={isMobile ? "M" : undefined}
          color="green100"
          secondaryColor="green500"
          size="M"
        />
        <WSText.Label color="grey700">
          Good work! You completed all the ToDos
        </WSText.Label>
      </WSFlexBox.CenterY>
    </WSPanel>
  );
};

export const InProgressEmptyState: React.FC = () => {
  const isMobile = useIsMobile();
  return (
    <WSPanel>
      <WSFlexBox.CenterY
        wrap="nowrap"
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "flex-start" : "center"}
      >
        <WSCircle.Icon
          icon="inProgress"
          mr={isMobile ? undefined : "M"}
          mb={isMobile ? "M" : undefined}
          color="blue100"
          secondaryColor="blue500"
          size="M"
        />
        <WSText.Label color="grey700">
          Started tasks display here so you can jump back in to complete
        </WSText.Label>
      </WSFlexBox.CenterY>
    </WSPanel>
  );
};

export const DoneEmptyState: React.FC = () => {
  const isMobile = useIsMobile();
  return (
    <WSPanel>
      <WSFlexBox.CenterY
        wrap="nowrap"
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "flex-start" : "center"}
      >
        <WSCircle.Icon
          icon="successMark"
          mr={isMobile ? undefined : "M"}
          mb={isMobile ? "M" : undefined}
          color="green100"
          secondaryColor="green500"
          size="M"
        />
        <WSText.Label color="grey700">
          Completed tasks display here
        </WSText.Label>
      </WSFlexBox.CenterY>
    </WSPanel>
  );
};

export const DashboardToDo: React.FC<Props> = ({
  title,
  description,
  icon,
  size = "M",
  canMarkAsDone,
  activityEventName,
  status,
  onClick,
  recommended,
  todoType,
  category,
  feature,
  ...elementProps
}) => {
  const isMobile = useIsMobile();
  const userId = useUserId();
  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const isCompleted = status === ToDoStatus.Completed;
  return (
    <WSPanel
      className={styles.todo}
      data-test-type={todoType}
      data-test-category={category}
      data-test-feature={feature}
      onClick={isCompleted ? undefined : onClick}
      {...elementProps}
    >
      <WSFlexBox
        wrap="nowrap"
        direction={isMobile ? "column" : "row"}
        alignItems={isMobile ? "flex-start" : "center"}
      >
        <WSCircle.Icon
          icon={isCompleted ? "successMark" : icon}
          mr={isMobile ? undefined : "M"}
          mb={isMobile ? "M" : undefined}
          color={isCompleted ? "green100" : "blue100"}
          secondaryColor={isCompleted ? "green500" : "blue500"}
          size={size}
        />
        <WSElement className={styles.taskContent}>
          <WSFlexBox.CenterY>
            <WSText
              kind={size === "L" ? "Heading3" : "Heading4"}
              mb="XS"
              className={cn(styles.title, {
                [styles.completed]: isCompleted
              })}
            >
              {title}
            </WSText>
            {recommended && (
              <WSBadge
                ml="S"
                backgroundColor="grey300"
                text="Start here!"
                textProps={{ color: "grey800" }}
              />
            )}
          </WSFlexBox.CenterY>
          <WSText
            color="grey600"
            kind={size === "S" ? "Label" : "Paragraph"}
            className={cn(styles.description, {
              [styles.completed]: isCompleted
            })}
          >
            {description}
          </WSText>
        </WSElement>
        <WSElement mt={isMobile ? "S" : "NONE"}>
          {canMarkAsDone && status !== ToDoStatus.Completed ? (
            <WSButton.Link
              loading={updateActivityMeta.isLoading}
              onClick={async e => {
                e.stopPropagation();
                const request = {
                  events: {
                    [activityEventName as string]: ToDoStatus.Completed
                  }
                };
                await updateActivity(request);
              }}
            >
              Mark as done
            </WSButton.Link>
          ) : null}
        </WSElement>
      </WSFlexBox>
    </WSPanel>
  );
};
